import React, { useEffect, useRef, useState } from "react";
import Cookies from "js-cookie";
import { embedDashboard } from "@superset-ui/embedded-sdk";

const DASHBOARD_MAP = {
  plant_manager: "e9493fef-c98a-41ae-8e6b-9bdd89c1abdb",
  diamond: "e9493fef-c98a-41ae-8e6b-9bdd89c1abdb",
};

const SUPERSET_DOMAIN = "https://reports.ascendcargo.com";

function getAuthToken() {
  return Cookies.get("authToken") || localStorage.getItem("authToken") || "";
}

async function requestGuestToken() {
  const authToken = getAuthToken();
  if (!authToken) {
    throw new Error("Sign in required to load embedded analytics.");
  }

  const apiBase =
    process.env.REACT_APP_API_BASE_URL || "https://demo-apps.ascendcargo.com:4445";

  const response = await fetch(`${apiBase}/api/superset/guest-token`, {
    headers: { Authorization: `Bearer ${authToken}` },
  });

  if (!response.ok) {
    throw new Error(`Guest token request failed (${response.status}).`);
  }

  const data = await response.json();
  const guest = data?.token ?? data?.guest_token ?? data?.guestToken;

  if (typeof guest !== "string" || !guest.trim()) {
    throw new Error("Server did not return a valid Superset guest token.");
  }

  return guest.trim();
}

const Analytics = () => {
  const containerRef = useRef(null);
  const [status, setStatus] = useState("loading");
  const [errorMessage, setErrorMessage] = useState("");

  const getUserRole = () => "diamond";

  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `
      .superset-embed iframe {
        width: 100% !important;
        height: 100% !important;
        border: none;
      }
    `;
    document.head.appendChild(style);
    return () => style.remove();
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!containerRef.current) return;

      setStatus("loading");
      setErrorMessage("");

      try {
        const guestToken = await requestGuestToken();
        if (cancelled || !containerRef.current) return;

        const dashboardId = DASHBOARD_MAP[getUserRole()];

        await embedDashboard({
          id: dashboardId,
          supersetDomain: SUPERSET_DOMAIN,
          mountPoint: containerRef.current,
          fetchGuestToken: async () => {
            const token = await requestGuestToken();
            if (typeof token !== "string" || !token) {
              throw new Error("Invalid guest token refresh.");
            }
            return token;
          },
          dashboardUiConfig: {
            hideTitle: true,
          },
        });

        if (!cancelled) setStatus("ready");
      } catch (err) {
        console.error("Superset embed failed:", err);
        if (!cancelled) {
          setStatus("error");
          setErrorMessage(
            err?.message ||
              "Analytics dashboard could not be loaded. Use Resource Selection → KPI Library for KPI catalog management."
          );
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="flex flex-col w-full h-full">
      <div className="pl-fluid-2 rounded-xl w-fit">
        <h4 className="text-fluid-lg font-semibold mt-2">Analytics</h4>
      </div>

      {status === "error" ? (
        <div className="mx-fluid-2 mt-4 max-w-xl rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
          <p className="font-semibold">Dashboard unavailable</p>
          <p className="mt-1">{errorMessage}</p>
          <p className="mt-2 text-amber-900/80">
            KPI library management is under{" "}
            <strong>Resource Selection → KPI Library</strong>.
          </p>
        </div>
      ) : null}

      {status === "loading" ? (
        <p className="px-fluid-2 py-4 text-sm text-gray-500">Loading analytics dashboard…</p>
      ) : null}

      <div
        className="superset-embed"
        ref={containerRef}
        style={{
          width: "100%",
          height: "calc(100vh - 60px)",
          display: status === "error" ? "none" : "block",
        }}
      />
    </div>
  );
};

export default Analytics;
